import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  saveReviewToOfflineQueue,
  syncOfflineReviews,
} from "@/utils/offlineReviewQueue";

export interface CreateReviewData {
  customer_name: string;
  rating: number;
  review_text: string;
  type: string;
  product_id?: string;
  lat?: string | number | null;
  lan?: string | number | null;
}

export interface CreateReviewResponse {
  status: string;
  message: string;
  data: {
    id: string;
    customer_name: string;
    rating: number;
    review_text: string;
    review_image_url: string | null;
    type: string;
    product: any;
    approved_at: string;
    created_at: string;
  };
}

export const useCreateReview = () => {
  const queryClient = useQueryClient();

  return useMutation<CreateReviewResponse, Error, CreateReviewData>({
    mutationFn: async (data) => {
      // If client is currently offline, queue immediately
      if (typeof navigator !== "undefined" && !navigator.onLine) {
        saveReviewToOfflineQueue(data);
        return {
          status: "queued_offline",
          message: "Review saved offline and will be synced when internet is restored",
          data: {
            id: `offline_${Date.now()}`,
            customer_name: data.customer_name,
            rating: data.rating,
            review_text: data.review_text,
            review_image_url: null,
            type: data.type,
            product: null,
            approved_at: "",
            created_at: new Date().toISOString(),
          },
        };
      }

      try {
        const response = await fetch(
          `https://back.sewargourmet.com/api/v1/reviews`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify(data),
            signal: typeof AbortSignal !== "undefined" && "timeout" in AbortSignal ? AbortSignal.timeout(2500) : undefined,
          },
        );

        if (!response.ok) {
          // If server error or bad network response, save to offline queue as fallback
          saveReviewToOfflineQueue(data);
          return {
            status: "queued_offline",
            message: "Review saved offline for retry",
            data: {
              id: `offline_${Date.now()}`,
              customer_name: data.customer_name,
              rating: data.rating,
              review_text: data.review_text,
              review_image_url: null,
              type: data.type,
              product: null,
              approved_at: "",
              created_at: new Date().toISOString(),
            },
          };
        }

        const json = await response.json();
        // Also trigger sync for any other pending reviews
        syncOfflineReviews();
        return json;
      } catch (err) {
        // Network failure (offline, timeout, DNS failure): queue for later
        saveReviewToOfflineQueue(data);
        return {
          status: "queued_offline",
          message: "Review saved offline and will sync automatically",
          data: {
            id: `offline_${Date.now()}`,
            customer_name: data.customer_name,
            rating: data.rating,
            review_text: data.review_text,
            review_image_url: null,
            type: data.type,
            product: null,
            approved_at: "",
            created_at: new Date().toISOString(),
          },
        };
      }
    },
    onSuccess: () => {
      // Invalidate reviews query to refetch data
      queryClient.invalidateQueries({ queryKey: ["reviews"] });
    },
  });
};
